import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { shortcuts } from '../app.shortcuts';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet, RouterModule } from '@angular/router';

// PrimeNG Modules for this page
import { ScrollPanel, ScrollPanelModule } from 'primeng/scrollpanel';
import { FormsModule } from '@angular/forms'; // For chat input
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ProgressBarModule } from 'primeng/progressbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { InputGroupModule } from 'primeng/inputgroup'; // For chat input
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { Subscription } from 'rxjs';
import { Chart, ChartDataset, ChartData } from 'chart.js';
import { ChatSessionService } from '../services/chat-session.service';
import { ChatSession } from '../models/chat-session.model';
import { SplitterModule } from 'primeng/splitter';
import { MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AiStudioService } from './ai-studio.service';
import { DomSanitizer } from '@angular/platform-browser';
import { MenubarModule } from 'primeng/menubar';
import { ChatMessage } from '../models/chat-message.model';
import { AppUser } from '../users/user.model';
import { ChipModule } from 'primeng/chip'; // Import ChipModule
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete'; // Import AutoCompleteModule
import { UserService } from '../users/users.service';
import { IndexedDbService } from '../services/indexed-db.service'; // Import IndexedDbService
import { ApprovalCardComponent } from '../chat-widgets/approval-card/approval-card.component'; // Import the new component
import { filter, map, take } from 'rxjs/operators';
import { Shortcut } from '../models/shortcut.model';
import { TableCardComponent } from '../chat-widgets/table-card/table-card.component';
import { WorkflowApproval } from '../chat-widgets/approval-card/workflow-apporval.model';
import { ChartCardComponent } from "../chat-widgets/chart-card/chart-card.component";
import { TaskCardComponent } from "../chat-widgets/task-card/task-card.component";
import { getCanvasOutletPathFromChildren } from './canvas-outlet-path.util';

@Component({
  selector: 'app-ai-studio',
  imports: [
    CommonModule,
    BreadcrumbModule,
    RouterModule,
    ToastModule,
    SplitterModule,
    ProgressSpinnerModule,
    ProgressBarModule,
    TableModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    TextareaModule,
    RouterOutlet, // For the secondary router-outlet
    ScrollPanelModule,
    FormsModule,
    ButtonModule,
    ToolbarModule,
    MenubarModule, // Add ContextMenuModule
    InputGroupModule,
    AutoCompleteModule, // Add AutoCompleteModule
    ChipModule, // Add ChipModule
    ApprovalCardComponent, // Add the new ApprovalCardComponent
    TableCardComponent // Add the new TableCardComponent
    ,
    ChartCardComponent,
    TaskCardComponent
],
  providers: [
    MessageService
  ],
  standalone: true,
  templateUrl: './ai-studio.component.html',
  styleUrl: './ai-studio.component.css'
})
export class AiStudioComponent implements OnInit, OnDestroy {

  @ViewChild('chatMessages')
  private chatMessagesContainer!: ElementRef;

  @ViewChild('chatScrollPanel')
  private chatScrollPanel!: ScrollPanel;

  @ViewChild('autoComplete') // Add ViewChild for the p-autoComplete component
  private autoComplete!: AutoComplete;

  isLoading = false;
  showUploadMenu = false;

  // For dynamic splitter panel sizes
  splitterPanelSizes: number[] = [40, 60]; // Default: chat pane is 60%, canvas is 40%
  readonly defaultCanvasViewPath = 'canvas-view';

  currentUser: AppUser = {} as AppUser;
  session: ChatSession | undefined;
  chatPrompt: string = '';
  chatTextString: string = '';
  private chartRendered: boolean = false;
  private routeSub!: Subscription;
  private routeSyncSub!: Subscription;
  private sessionDataSub!: Subscription; // To listen for updates to this session's data
  private messageSub!: Subscription; // To listen for new messages from the service

  // For Page Visibility API
  private visibilityChangeHandler: (() => void) | undefined;
  isLoadingCurrentSession: boolean = false; // To track loading of the specific session

  // For @mention functionality
  allUsersForMention: AppUser[] = []; // To store all users that can be mentioned
  isMentioning: boolean = false; // Flag to indicate if user is in mention mode
  mentionQuery: string = ''; // Current query for mention

  isListening: boolean = false;
  speechRecognition: any;
  lastMessageWasVoice: boolean = false;

  // --- NEW: For /shortcut functionality ---
  isShortcutMode: boolean = false; // Flag to indicate if user is in /shortcut mode
  shortcutQuery: string = ''; // Current query for shortcut

  // Generic suggestions array for the p-autoComplete component
  suggestions: (AppUser | Shortcut)[] = [];

  constructor(
    private route: ActivatedRoute, private messageService: MessageService,
    private changeDetectorRef: ChangeDetectorRef, private sanitizer: DomSanitizer,
    private router: Router, private aiStudioService: AiStudioService,
    public sessionService: ChatSessionService, // Keep for session management
    private userService: UserService, // Inject UserService
    private indexedDbService: IndexedDbService, // Inject IndexedDbService
    private cd: ChangeDetectorRef,
    private location: Location, private zone: NgZone
  ) { }

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe(params => {
      const sessionIdParam = params.get('sessionId'); // Get as string
      if (sessionIdParam) {
        const sessionId = +sessionIdParam; // Convert to number
        if (!isNaN(sessionId)) {
          this.loadSessionData(sessionId);
        } else {
          console.error("Invalid sessionId from route params:", sessionIdParam);
          // Handle error, e.g., navigate away or show message
        }
      }
      this.currentUser = JSON.parse(sessionStorage.getItem('appUser') ?? '') as AppUser;
    });

    this.routeSyncSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.syncActiveCanvasPathFromRoute();
    });

    // Fetch users for mention suggestions
    this.loadUsersForMention();
    this.setupSpeechRecognition();
    this.setupVisibilityChangeListener();

    // Subscribe to newly added messages to perform UI actions like navigation or scrolling.
    // This handles messages from both the current user's actions and from external sources like FCM.
    this.messageSub = this.sessionService.messageAdded$.pipe(
      filter(message => !!this.session && this.session.sessionId === message.sessionId)
    ).subscribe(message => {
      this.isLoading = message.isPartial;
      if (message.activeCanvasPath) {
        this.navigateToCanvas(message.activeCanvasPath);
      }
      this.scrollToBottom();
      // Trigger change detection to ensure UI updates for dynamic content like charts
      // that are parsed and added to the message object in the service.
      this.changeDetectorRef.detectChanges();
    });

  }

  loadUsersForMention(): void {
    // Fetch users using the UserService
    // You might want to add pagination or filtering parameters here if the user list is very large.
    // For simplicity, fetching all users (or the first page).
    this.userService.getAllUsers({
      page: 0,
      size: 1000,
    }).subscribe({
      next: (page) => {
        this.allUsersForMention = page.content.filter(u => u.id !== this.currentUser?.id); // Exclude self
      },
      error: (err) => {
        console.error('Error loading users for mention:', err);
        this.allUsersForMention = []; // Clear or handle error appropriately
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load users for mentions.' });
      }
    });
  }

  loadSessionData(sessionId: number): void {
    this.isLoadingCurrentSession = true;
    this.session = undefined; // Clear previous session data to avoid showing stale content
    this.sessionDataSub?.unsubscribe(); // Unsubscribe from any previous reactive updates

    // getSession is the single source of truth for loading a session.
    // It handles the cache-then-network logic internally.
    this.sessionService.getSession(sessionId).subscribe({
      next: (loadedSession) => {
        this.isLoadingCurrentSession = false;

        if (!loadedSession) {
          // If the service definitively says the session doesn't exist (after checking cache and network)
          this.messageService.add({ severity: 'error', summary: 'Error', detail: `Session ${sessionId} could not be loaded.` });
          this.router.navigate(['../'], { relativeTo: this.route.parent });
          return;
        }

        // We have the session. Assign it.
        this.session = loadedSession;

        // Now that we have the session, set up a listener for any *further* live updates.
        this.listenForLiveSessionUpdates(sessionId);

        // Perform initial setup actions that depend on the session data.
        const currentCanvasPath = getCanvasOutletPathFromChildren(this.route.children);
        const desiredCanvasPath = this.session.activeCanvasPath || this.defaultCanvasViewPath;
        if (currentCanvasPath !== desiredCanvasPath) {
          this.navigateToCanvas(desiredCanvasPath);
        }
        this.scrollToBottom(true);
      },
      error: (err) => {
        this.isLoadingCurrentSession = false;
        console.error(`[AiStudioComponent] Error loading session ${sessionId}:`, err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: `Failed to load session ${sessionId}.` });
        this.router.navigate(['../'], { relativeTo: this.route.parent });
      }
    });
  }

  private listenForLiveSessionUpdates(sessionId: number): void {
    this.sessionDataSub = this.sessionService.sessions$.pipe(
      map(allSessions => allSessions.find(s => s.sessionId === sessionId)),
      filter((session): session is ChatSession => !!session) // Type guard to ensure session is not undefined
    ).subscribe(updatedSession => {
      // This subscription will fire whenever the session data changes in the service's main list.
      // We can update our component's session object with the latest data.
      this.session = updatedSession;
      this.cd.detectChanges(); // Trigger change detection as this can happen in the background.
    });
  }

  addNewSession() {
    this.isLoading = true;
    this.sessionService.addSession().subscribe({
      next: (newSession) => {
        this.isLoading = false;
        if (newSession && newSession.sessionId) {
          // Navigate to the new session's route.
          // This will trigger AiStudioComponent's ngOnInit and loadSessionData for the new session.
          this.router.navigate(['session', newSession.sessionId], { relativeTo: this.route.parent });
        } else {
          // This case handles if addSession returns null (e.g., no user ID) or if the session object is malformed.
          console.error("Failed to create a new session or session ID is missing.");
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not create new session. Please try again.' });
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error("Error creating new session:", err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'An error occurred while creating the session.' });
      }
    });
  }

  navigateToCanvas(canvasPath: string): void {
    if (this.session) {
      const normalizedCanvasPath = canvasPath || this.defaultCanvasViewPath;
      this.session.activeCanvasPath = normalizedCanvasPath;
      this.persistCanvasPath(normalizedCanvasPath); // Update service

      // Determine the segments for the canvasOutlet based on canvasPath
      const canvasSegments = normalizedCanvasPath.split('/');
      this.router.navigate(['.', { outlets: { canvasOutlet: canvasSegments } }], { relativeTo: this.route });

      // Adjust panel sizes based on the canvas being navigated to
      if (canvasSegments.length > 0) { // Specific view that needs more canvas space
        this.splitterPanelSizes = [30, 70]; // Chat 40%, Canvas 60%
      } else { // Default or other views
        this.splitterPanelSizes = [60, 40]; // Chat 60%, Canvas 40%
      }
    }
  }

  private syncActiveCanvasPathFromRoute(): void {
    if (!this.session) {
      return;
    }

    const currentCanvasPath = getCanvasOutletPathFromChildren(this.route.children);
    if (!currentCanvasPath || currentCanvasPath === this.session.activeCanvasPath) {
      return;
    }

    this.session.activeCanvasPath = currentCanvasPath;
    this.persistCanvasPath(currentCanvasPath);
  }

  private persistCanvasPath(canvasPath: string): void {
    if (!this.session) {
      return;
    }

    this.sessionService.setSessionCanvasPath(this.session.sessionId, canvasPath).subscribe({
      error: (error) => {
        console.error(`Failed to persist canvas path for session ${this.session?.sessionId}:`, error);
      }
    });
  }

  /**
   * Populates the suggestions list based on the active mode (@mention or /shortcut).
   */
  searchSuggestions(event: AutoCompleteCompleteEvent): void {
    if (this.isMentioning) {
      const query = this.mentionQuery.toLowerCase();
      this.suggestions = this.allUsersForMention.filter(user =>
        (user.fullName?.toLowerCase().includes(query) || user.email.toLowerCase().includes(query))
      );
    } else if (this.isShortcutMode) {
      const query = this.shortcutQuery.toLowerCase();
      this.suggestions = shortcuts.filter(shortcut =>
        shortcut.command.toLowerCase().includes(query) || shortcut.description.toLowerCase().includes(query)
      );
    } else {
      this.suggestions = [];
    }
  }

  /**
   * Handles the selection of an item from the autocomplete list.
   */
  onSuggestionSelect(event: AutoCompleteSelectEvent): void {
    const selectedItem = event.value;

    if (this.isMentioning && 'firebaseId' in selectedItem) { // Type guard for AppUser
      const selectedUser = selectedItem as AppUser;
      this.onUserMentionSelect(selectedUser);

      const mentionText = `@{${selectedUser.fullName || 'User'}} `;
      const lastTriggerIndex = this.chatTextString.lastIndexOf('@' + this.mentionQuery);

      if (lastTriggerIndex !== -1) {
        this.chatPrompt = this.chatTextString.substring(0, lastTriggerIndex) + mentionText;
      } else {
        this.chatPrompt += mentionText;
      }
    } else if (this.isShortcutMode && 'command' in selectedItem) { // Type guard for Shortcut
      const selectedShortcut = selectedItem as Shortcut;

      if (selectedShortcut.action) {
        selectedShortcut.action(this);
        this.chatPrompt = ''; // Clear prompt after executing an action
      } else if (selectedShortcut.insertText) {
        this.chatPrompt = selectedShortcut.insertText;
        // To send immediately, uncomment the next line
        // this.sendChatMessage();
      } else {
        // Default behavior: insert the command itself
        this.chatPrompt = selectedShortcut.command + ' ';
      }
    }

    this.resetAutocompleteState();
    // Set focus back to the input after selection
    setTimeout(() => this.autoComplete?.inputEL?.nativeElement.focus(), 0);
  }

  /**
   * Resets all autocomplete-related state properties.
   */
  private resetAutocompleteState(): void {
    if (this.isMentioning || this.isShortcutMode) {
      this.isMentioning = false;
      this.isShortcutMode = false;
      this.suggestions = [];
      this.mentionQuery = '';
      this.shortcutQuery = '';
    }
  }

  onUserMentionSelect(selectedUser: AppUser): void {
    if (this.session && selectedUser && typeof this.session.sessionId === 'number' && typeof selectedUser.id === 'number') {
      const userExistsInSession = this.session.users.find(u => u.id === selectedUser.id);
      if (!userExistsInSession) {
        // Add user to session locally (for immediate UI update)
        // The backend response will be the source of truth eventually.
        // this.session.users.push(selectedUser); // Optional: optimistic update

        this.sessionService.addUserToSession(this.session.sessionId, selectedUser.id).subscribe({
          next: (updatedSession) => {
            if (updatedSession) {
              this.session = updatedSession; // Update session with response from backend
              this.messageService.add({
                severity: 'info',
                summary: 'User Added',
                detail: `${selectedUser.fullName} has been added to this session.`
              });
            }
          },
          error: (err) => console.error('Failed to add user to session on backend', err)
        });
      }
    }
  }

  private setupVisibilityChangeListener(): void {
    if (typeof document !== 'undefined') { // Ensure document is available (for SSR safety, though less critical here)
      this.visibilityChangeHandler = () => {
        if (document.visibilityState === 'visible') {
          console.log('Tab became visible. Checking for active session to refresh.');
          if (this.session && typeof this.session.sessionId === 'number') {
            console.log(`Refreshing data for session ID: ${this.session.sessionId}`);
            //this.loadSessionData(this.session.sessionId);
          }
        }
      };
      document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.routeSyncSub?.unsubscribe();
    this.sessionDataSub?.unsubscribe();
    this.messageSub?.unsubscribe();

    if (this.visibilityChangeHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }

  /**
   * Handles key events on the input to detect trigger characters (@ or /)
   * and manage the autocomplete state. Also handles the Enter key press.
   */
  handleInput(event?: KeyboardEvent): void {
    const value = this.chatPrompt;
    this.chatTextString = this.chatPrompt;
    const lastAt = value.lastIndexOf('@');
    const lastSlash = value.lastIndexOf('/');
    const lastTriggerIndex = Math.max(lastAt, lastSlash);

    if (event && event.key === 'Escape') {
      this.resetAutocompleteState();
      return;
    }

    if (lastTriggerIndex !== -1 && !value.substring(lastTriggerIndex + 1).includes(' ')) {
      if (lastTriggerIndex === lastAt) {
        this.isMentioning = true;
        this.isShortcutMode = false;
        this.mentionQuery = value.substring(lastAt + 1);
      } else { // lastTriggerIndex === lastSlash
        this.isShortcutMode = true;
        this.isMentioning = false;
        this.shortcutQuery = value.substring(lastSlash + 1);
      }
    } else {
      this.resetAutocompleteState();
    }

    // Handle Enter key press
    if (event && event.key === 'Enter' && !event.shiftKey) {
      // Check if the autocomplete overlay is NOT visible before sending the message
      if (this.autoComplete && !this.autoComplete.overlayVisible) {
        event.preventDefault(); // Prevent default newline behavior only if sending message
        this.sendChatMessage();
      } else if (this.autoComplete && this.autoComplete.overlayVisible) {
        // If overlay is visible, prevent default newline but let p-autocomplete handle selection
        event.preventDefault();
      }
    }
    // If Shift+Enter, default behavior (new line) will occur.
  }

  sendChatMessage() {
    this.isLoading = true;
    if (this.chatPrompt.trim() === '') {
      this.isLoading = false;
      return;
    }
    let chatMessage = new ChatMessage(this.chatPrompt, this.currentUser, this.session?.sessionId ?? -1);
    chatMessage.timestamp = new Date();
    chatMessage.success = true;
    if (this.session) { // Ensure session exists before accessing users
      chatMessage.segments = this.sessionService.convertToMessageSegments(chatMessage.textContent, this.session.users || []);
    }
    this.session?.chatMessages.push(chatMessage);
    this.scrollToBottom(true);
    console.log(chatMessage);

    this.aiStudioService.sendAPIMessage(chatMessage).subscribe({
      next: (response) => {
        chatMessage.messageId = this.processChatMessage(response);
        this.indexedDbService.cacheChatMessage(chatMessage);
      },
      error: (error) => {
        console.error('Error sending message:', error);
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'An error occurred while sending the message.' + error.message });
      }
    });
    this.chatPrompt = ''; // Clear input field
  }

  private processChatMessage(response: ChatMessage): number | undefined {
    // This method is now much simpler. Its only job is to delegate the new message
    // from the API to the session service. The service will handle all processing
    // and state updates. The component will react to those updates via its subscriptions.
    console.log(response);
    if (response.activeCanvasPath)
      this.navigateToCanvas(response.activeCanvasPath);
    this.isLoading = response.isPartial;

    if (!response.textContent) {
      return response.requestMessageId;
    }

    if (this.session) {
      // The service will process, update state, and emit events that our subscriptions will catch.
      this.sessionService.addReceivedChatMessageToSession(response);
    }

    this.scrollToBottom(true);
    return response.requestMessageId;
  }

  private scrollToBottom(force: boolean = false): void {
    // Use a timeout to ensure the DOM has been updated with the new message
    // and the scroll panel has had time to update its internal state.
    setTimeout(() => {
      try {
        // Ensure the scroll panel component is available
        if (!this.chatScrollPanel) {
          return;
        }
        const scrollContainer = this.chatScrollPanel?.contentViewChild?.nativeElement;
        const scrollThreshold = 150; // Pixel threshold to consider "at the bottom"
        // Check if the user is scrolled up before the new message was added
        const atBottom = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight <= scrollThreshold;
        // If forced, or if the user was already at the bottom, scroll down.
        if (force || atBottom) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
        }
      } catch (err) {
        console.warn('Could not scroll to bottom:', err);
      }
    }, 10); // A small delay is often more reliable than 0
  }

  downloadFile(fileUrl: string, fileName?: string) {
    // data:/blob: URLs مش بتفتح بـ window.open، فلازم anchor بـ download
    if (fileName) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.click();
      link.remove();
      return;
    }

    window.open(fileUrl, '_blank');
  }

  // --- Approval Card Handlers ---
  onOpenApprovalDetails(workflowApproval: WorkflowApproval): void {
    if (workflowApproval.resourceType?.resourceName === 'Promo Plan') {
      console.log(`Opening details for Promo Plan ID: ${workflowApproval.businessObjectId}`);
      // Navigate to the promo plan view page in the canvas
      this.navigateToCanvas(`promo-plan/view/${workflowApproval.businessObjectId}`);
      this.messageService.add({
        severity: 'info',
        summary: 'Opening Details',
        detail: `Navigating to Promo Plan ${workflowApproval.businessObjectId} details.`
      });
    } else if (workflowApproval.resourceType?.resourceName === 'Settlement') {
      console.log(`Opening details for Settlement ID: ${workflowApproval.businessObjectId}`);
      // Navigate to the settlement view page in the canvas
      this.navigateToCanvas(`settlement/view/${workflowApproval.businessObjectId}`);
      this.messageService.add({
        severity: 'info',
        summary: 'Opening Details',
        detail: `Navigating to Settlement ${workflowApproval.businessObjectId} details.`
      });
    }
  }

  onViewTableDetails(canvasPath: string): void {
    console.log(`Viewing details for Promo Plan ID from table: ${canvasPath}`);
    this.navigateToCanvas(canvasPath);
  }

  goBack(): void {
    this.location.back();
  }

  setupSpeechRecognition() {
    if (typeof window !== 'undefined' && 'webkitSpeechRecognition' in window) {
      this.speechRecognition = new (window as any).webkitSpeechRecognition();
      this.speechRecognition.continuous = false;
      // By not setting lang, it defaults to the browser's language setting.
      this.speechRecognition.interimResults = false;
      this.speechRecognition.maxAlternatives = 1;

      this.speechRecognition.onstart = () => {
        this.zone.run(() => {
          this.isListening = true;
          this.cd.detectChanges();
        });
      };

      this.speechRecognition.onresult = (event: any) => {
        this.zone.run(() => {
          const transcript = event.results[0][0].transcript;
          this.chatPrompt = this.chatPrompt ? this.chatPrompt + transcript : transcript;
          this.cd.detectChanges();
        });
      };

      this.speechRecognition.onend = () => {
        this.zone.run(() => {
          this.isListening = false;
          if (this.chatPrompt && this.chatPrompt.trim().length > 0) {
            this.sendChatMessage();
          }
          this.cd.detectChanges();
        });
      };

      this.speechRecognition.onerror = (event: any) => {
        this.zone.run(() => {
          console.error('Speech recognition error', event.error);
          this.isListening = false;
          this.messageService.add({ severity: 'error', summary: 'Speech Error', detail: `Could not recognize speech: ${event.error}` });
          this.cd.detectChanges();
        });
      };
    }
  }

  toggleSpeechRecognition() {
    if (!this.speechRecognition) {
      this.messageService.add({ severity: 'warn', summary: 'Unsupported', detail: 'Speech recognition is not supported by your browser.' });
      return;
    }

    if (this.isListening) {
      this.speechRecognition.stop();
    } else {
      this.lastMessageWasVoice = true; // Set the flag here
      this.speechRecognition.start();
    }
  }

  onSendButtonClick() {
    this.lastMessageWasVoice = false;
    this.sendChatMessage();
  }

  reloadPage(): void {
    if (this.session && this.session.sessionId) {
      this.loadSessionData(this.session.sessionId);
    }
  }



}
